import type { CallbackInterface } from 'recoil'

import { pageState } from 'State/page'

import type { PageStateType } from 'Types/state'

import { logAppError } from 'Helpers/logger'

import getIntl from 'Helpers/intl'

import type { SelectionState } from 'Pages/RecipientsList/model'
import {
  bulkExportCSV,
  notifyBulkExportSuccess,
} from 'Pages/RecipientsList/recoil/services/bulk'
import { columnsState } from 'Pages/RecipientsList/recoil/state/columns'
import { appliedSearchFiltersState } from 'Pages/RecipientsList/recoil/state/filters'
import {
  isSelectAllCheckedState,
  isSelectAllIndeterminateState,
  paginationState,
  resultsState,
  selectionState,
} from 'Pages/RecipientsList/recoil/state/results'

export const bulkSelectAllCallback =
  ({ set }: CallbackInterface) =>
  async () => {
    set<SelectionState>(selectionState, (prev) => ({
      ...prev,
      mode: 'all',
    }))
  }

export const bulkSelectVisibleCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    const results = await snapshot.getPromise(resultsState)

    set<SelectionState>(selectionState, ({ checked, ...prev }) => ({
      ...prev,
      mode: 'selection',
      checked: Array.from(new Set([...checked, ...results.map((r) => r.Id)])),
    }))
  }

export const bulkSelectNoneCallback =
  ({ set }: CallbackInterface) =>
  async () => {
    set<SelectionState>(selectionState, {
      mode: 'selection',
      checked: [],
      unchecked: [],
    })
  }

export const toggleSelectAllRecipientsCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    const [isAllChecked, isIndeterminate, results] = await Promise.all([
      snapshot.getPromise(isSelectAllCheckedState),
      snapshot.getPromise(isSelectAllIndeterminateState),
      snapshot.getPromise(resultsState),
    ])

    const resultsIds: number[] = results.map((r) => r.Id)

    set<SelectionState>(selectionState, ({ checked, ...prev }) => ({
      ...prev,
      checked:
        isAllChecked || isIndeterminate
          ? checked.filter((c) => !resultsIds.includes(c))
          : [...checked, ...resultsIds],
    }))
  }

export const toggleSelectRecipientCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (idRecipient: number) => {
    const { checked } = await snapshot.getPromise(selectionState)

    const isChecked: boolean = checked.includes(idRecipient)

    set<SelectionState>(selectionState, ({ checked, ...prev }) => ({
      ...prev,
      checked: isChecked
        ? checked.filter((i) => i !== idRecipient)
        : [...checked, idRecipient],
    }))
  }

//

export const bulkExportCSVCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (fileType: 'csv' | 'xlsx') => {
    set(pageState, (prev) => ({
      ...prev,
      loading: true,
    }))

    const [
      { mode, checked },
      searchFilters,
      columns,
      { orderBy, orderDescending },
    ] = await Promise.all([
      snapshot.getPromise(selectionState),
      snapshot.getPromise(appliedSearchFiltersState),
      snapshot.getPromise(columnsState),
      snapshot.getPromise(paginationState),
    ])

    try {
      await bulkExportCSV({
        mode,
        checked,
        searchFilters,
        columns,
        orderBy,
        orderDescending,
        fileType,
      })

      notifyBulkExportSuccess()
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'bulkExportCSVCallback',
        appError: error,
      })

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'error',
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric',
        }),
      }))
    }

    set(pageState, (prev) => ({
      ...prev,
      loading: false,
    }))
  }
